class OrganaxSearch extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('.organax-search__form');
    this.input = this.form.querySelector('input[type="search"]');
    this.mobileToggle = this.querySelector('.organax-search__mobile-toggle');
    this.backdrop = this.querySelector('.organax-search__backdrop');
    this.results = this.querySelector('.organax-search__results');
    this.abortController = null;
    this.searchTimer = null;
    this.documentPointerHandler = this.onDocumentPointerDown.bind(this);
    this.desktop = matchMedia('(min-width: 750px)');
    this.desktopChangeHandler = () => {
      if (this.hasAttribute('open')) this.prepareRecommendations();
    };
    this.desktop.addEventListener('change', this.desktopChangeHandler);

    this.input.addEventListener('focus', () => this.open());
    this.input.addEventListener('click', () => this.open());
    this.input.addEventListener('input', () => this.onInput());
    this.form.addEventListener('submit', (event) => {
      if (!this.input.value.trim()) event.preventDefault();
    });
    this.mobileToggle.addEventListener('click', () => {
      if (this.hasAttribute('open')) this.close(true);
      else {
        this.open();
        requestAnimationFrame(() => this.input.focus());
      }
    });
    this.backdrop.addEventListener('click', () => this.close());
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close(true);
      }
    });
    document.addEventListener('pointerdown', this.documentPointerHandler);
  }

  disconnectedCallback() {
    clearTimeout(this.searchTimer);
    this.abortController?.abort();
    document.removeEventListener('pointerdown', this.documentPointerHandler);
    this.desktop?.removeEventListener('change', this.desktopChangeHandler);
    document.body.classList.remove('organax-search-open');
  }

  open() {
    document.querySelector('.organax-nav__root[open]')?.removeAttribute('open');
    this.prepareRecommendations();
    this.setAttribute('open', '');
    this.input.setAttribute('aria-expanded', 'true');
    this.mobileToggle.setAttribute('aria-expanded', 'true');
    this.mobileToggle.setAttribute('aria-label', 'Close search');
    document.body.classList.add('organax-search-open');
  }

  prepareRecommendations() {
    // Mobile idle search intentionally shows links only. Keep desktop-only
    // cards out of image loading and automatic price scans until needed.
    if (!this.desktop.matches) return;
    const stored = this.querySelector('template[data-search-recommendations]');
    if (stored) stored.replaceWith(stored.content);
  }

  close(restoreFocus = false) {
    if (!this.hasAttribute('open')) return;
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.mobileToggle.setAttribute('aria-expanded', 'false');
    this.mobileToggle.setAttribute('aria-label', 'Search');
    document.body.classList.remove('organax-search-open');
    if (restoreFocus && matchMedia('(max-width: 749px)').matches) this.mobileToggle.focus();
  }

  onDocumentPointerDown(event) {
    if (this.hasAttribute('open') && !this.contains(event.target)) this.close();
  }

  onInput() {
    const query = this.input.value.trim();
    clearTimeout(this.searchTimer);
    this.abortController?.abort();
    this.open();

    if (!query) {
      this.dataset.hasQuery = 'false';
      this.results.innerHTML = '';
      this.results.setAttribute('aria-busy', 'false');
      return;
    }

    this.dataset.hasQuery = 'true';
    this.results.setAttribute('aria-busy', 'true');
    this.results.innerHTML = '<p class="organax-search__loading">Searching…</p>';
    this.searchTimer = setTimeout(() => this.fetchResults(query), 250);
  }

  async fetchResults(query) {
    this.abortController = new AbortController();
    const parameters = new URLSearchParams({
      q: query,
      section_id: 'organax-2026-predictive-search',
      'resources[type]': 'product,query',
      'resources[limit]': '8',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,vendor,variants.title',
    });

    try {
      const response = await fetch(`${routes.predictive_search_url}?${parameters}`, {
        signal: this.abortController.signal,
      });
      if (!response.ok) throw new Error(`Search request failed: ${response.status}`);
      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const section = documentFragment.querySelector('#shopify-section-organax-2026-predictive-search');
      if (!section) throw new Error('Search response section was missing');
      if (this.input.value.trim() !== query) return;
      this.results.innerHTML = section.innerHTML;
      this.results.setAttribute('aria-busy', 'false');
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (this.input.value.trim() !== query) return;
      this.results.setAttribute('aria-busy', 'false');
      const message = document.createElement('p');
      message.className = 'organax-search__error';
      message.textContent = 'Search is temporarily unavailable. ';
      const link = document.createElement('a');
      const url = new URL(this.form.action);
      url.searchParams.set('q', query);
      link.href = url.href;
      link.textContent = 'Show all results';
      message.append(link);
      this.results.replaceChildren(message);
    }
  }
}

customElements.define('organax-search', OrganaxSearch);
